import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';

// Bits per unit
const SIZE = { B:8, KB:8*1024, MB:8*1024**2, GB:8*1024**3, TB:8*1024**4, PB:8*1024**5, bit:1, Kbit:1024, Mbit:1024**2, Gbit:1024**3 };
const SPEED = { bps:1, Kbps:1000, Mbps:1e6, Gbps:1e9, Tbps:1e12, 'B/s':8, 'KB/s':8*1000, 'MB/s':8e6, 'GB/s':8e9 };

export default function BandwidthCalc() {
  const [mode, setMode] = useState('download'); // download | unit | website | hosting
  const [size, setSize] = useState(500);
  const [sizeUnit, setSizeUnit] = useState('MB');
  const [speed, setSpeed] = useState(50);
  const [speedUnit, setSpeedUnit] = useState('Mbps');
  const [convVal, setConvVal] = useState(1);
  const [convUnit, setConvUnit] = useState('MB');
  const [pageSize, setPageSize] = useState(2); // MB per page
  const [pageUnit, setPageUnit] = useState('MB');
  const [visits, setVisits] = useState(1000); // visits/day
  const [pagesPerVisit, setPagesPerVisit] = useState(3);

  const download = useMemo(() => {
    const bits = Number(size) * SIZE[sizeUnit];
    const bps = Number(speed) * SPEED[speedUnit];
    if (!bps) return null;
    const sec = bits / bps;
    const h = Math.floor(sec/3600); const m = Math.floor((sec%3600)/60); const s = sec%60;
    return { sec, h, m, s: s.toFixed(1) };
  }, [size, sizeUnit, speed, speedUnit]);

  const bytesConv = Number(convVal) * SIZE[convUnit] / 8;
  const convRows = Object.entries(SIZE).filter(([u]) => !u.includes('bit')).map(([u,k]) => [u, (bytesConv * 8 / k)]);

  const website = useMemo(() => {
    const bytesPerPage = Number(pageSize) * (SIZE[pageUnit] / 8);
    const daily = bytesPerPage * Number(visits) * Number(pagesPerVisit);
    return {
      daily, monthly: daily * 30, yearly: daily * 365,
      persec: daily / 86400
    };
  }, [pageSize, pageUnit, visits, pagesPerVisit]);

  const humanBytes = (b) => {
    const u = ['B','KB','MB','GB','TB','PB']; let i=0; while (b >= 1024 && i < u.length-1) { b/=1024; i++; }
    return `${b.toFixed(2)} ${u[i]}`;
  };

  return (
    <CalcShell look="cnet" title="Bandwidth Calculator" subtitle="Estimate download time, convert data units and plan website bandwidth needs.">
      <div className="flex gap-2 border-b mb-4 flex-wrap">
        {[['download','Download / Upload Time'],['unit','Data Unit Converter'],['website','Website Bandwidth'],['hosting','Hosting Bandwidth Converter']].map(([v,l]) => (
          <button key={v} className={`px-4 py-2 text-sm font-semibold ${mode===v?'border-b-2 border-blue-600 text-blue-700':'text-slate-500'}`} onClick={() => setMode(v)}>{l}</button>
        ))}
      </div>

      {mode === 'download' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <div className="grid grid-cols-2 gap-3">
              <div className="field"><label>File Size</label><input type="number" value={size} onChange={e => setSize(e.target.value)}/></div>
              <div className="field"><label>Unit</label><select value={sizeUnit} onChange={e => setSizeUnit(e.target.value)}>{Object.keys(SIZE).map(u => <option key={u}>{u}</option>)}</select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="field"><label>Speed</label><input type="number" step="0.1" value={speed} onChange={e => setSpeed(e.target.value)}/></div>
              <div className="field"><label>Unit</label><select value={speedUnit} onChange={e => setSpeedUnit(e.target.value)}>{Object.keys(SPEED).map(u => <option key={u}>{u}</option>)}</select></div>
            </div>
          </div>
          <div>
            {download && (
              <div className="result-panel"><div className="result-title">Estimated Time</div><div className="result-value">{download.h}h {download.m}m {download.s}s</div><div className="text-sm text-slate-600 mt-1">{download.sec.toFixed(1)} seconds total</div></div>
            )}
          </div>
        </div>
      )}

      {mode === 'unit' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <div className="grid grid-cols-2 gap-3">
              <div className="field"><label>Value</label><input type="number" value={convVal} onChange={e => setConvVal(e.target.value)}/></div>
              <div className="field"><label>Unit</label><select value={convUnit} onChange={e => setConvUnit(e.target.value)}>{Object.keys(SIZE).map(u => <option key={u}>{u}</option>)}</select></div>
            </div>
          </div>
          <div>
            <table className="data-table">
              <thead><tr><th>Unit</th><th className="text-right">Value</th></tr></thead>
              <tbody>{convRows.map(([u,v]) => <tr key={u}><td>{u}</td><td className="text-right font-mono">{Number(v).toLocaleString('en-US',{maximumFractionDigits:6})}</td></tr>)}</tbody>
            </table>
          </div>
        </div>
      )}

      {mode === 'website' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <div className="grid grid-cols-2 gap-3">
              <div className="field"><label>Average Page Size</label><input type="number" step="0.1" value={pageSize} onChange={e => setPageSize(e.target.value)}/></div>
              <div className="field"><label>Unit</label><select value={pageUnit} onChange={e => setPageUnit(e.target.value)}>{['KB','MB','GB'].map(u => <option key={u}>{u}</option>)}</select></div>
            </div>
            <div className="field"><label>Daily Visitors</label><input type="number" value={visits} onChange={e => setVisits(e.target.value)}/></div>
            <div className="field"><label>Page Views per Visit</label><input type="number" value={pagesPerVisit} onChange={e => setPagesPerVisit(e.target.value)}/></div>
          </div>
          <div>
            <table className="data-table">
              <tbody>
                <tr><td>Per Second</td><td className="text-right">{humanBytes(website.persec)}/s</td></tr>
                <tr><td>Per Day</td><td className="text-right">{humanBytes(website.daily)}</td></tr>
                <tr><td>Per Month (30d)</td><td className="text-right">{humanBytes(website.monthly)}</td></tr>
                <tr><td>Per Year</td><td className="text-right">{humanBytes(website.yearly)}</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
      {mode === 'hosting' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <p className="text-sm text-slate-600 mb-3">Convert a hosting bandwidth allowance between common tiers and see the equivalent sustained data rate.</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="field"><label>Monthly Bandwidth</label><input type="number" value={convVal} onChange={e => setConvVal(e.target.value)}/></div>
              <div className="field"><label>Unit</label>
                <select value={convUnit} onChange={e => setConvUnit(e.target.value)}>{['MB','GB','TB','PB'].map(u => <option key={u}>{u}</option>)}</select>
              </div>
            </div>
          </div>
          <div>
            {(() => {
              const bytesMonth = Number(convVal) * SIZE[convUnit] / 8;
              const bytesDay = bytesMonth / 30;
              const bytesSec = bytesMonth / (30 * 86400);
              const kbps = (bytesSec * 8) / 1000;
              return (
                <table className="data-table"><tbody>
                  <tr><td>Per Day</td><td className="text-right">{humanBytes(bytesDay)}</td></tr>
                  <tr><td>Per Hour</td><td className="text-right">{humanBytes(bytesDay/24)}</td></tr>
                  <tr><td>Per Minute</td><td className="text-right">{humanBytes(bytesDay/1440)}</td></tr>
                  <tr><td>Per Second</td><td className="text-right">{humanBytes(bytesSec)}/s</td></tr>
                  <tr><td>Sustained Rate</td><td className="text-right font-semibold">{kbps.toFixed(2)} kbps</td></tr>
                  <tr><td>Sustained Rate</td><td className="text-right">{(kbps/1000).toFixed(4)} Mbps</td></tr>
                </tbody></table>
              );
            })()}
          </div>
        </div>
      )}
    </CalcShell>
  );
}
